import React, { useId, useMemo } from 'react';
import { fmtDur } from '../../lib/time.js';
import { buildSlices, buildTeamRows } from './chartUtils.js';
import { oneOf } from '../../lib/sessionState.js';
import { useSessionState } from '../../lib/useSessionState.js';

const CLIENT_MODES = oneOf(['clients', 'projects']);
const TEAM_MODES = oneOf(['teams', 'people']);

const OUTER_RADIUS = 84;
const INNER_RADIUS = 52;
const CENTER = 100;

function polar(radius, angle) {
  return [CENTER + radius * Math.cos(angle), CENTER + radius * Math.sin(angle)];
}

// Annulus segment from startAngle to endAngle (radians, clockwise from 12
// o'clock). Full-circle slices are drawn as a <circle> by the caller instead.
function arcPath(startAngle, endAngle) {
  const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
  const [x0, y0] = polar(OUTER_RADIUS, startAngle);
  const [x1, y1] = polar(OUTER_RADIUS, endAngle);
  const [x2, y2] = polar(INNER_RADIUS, endAngle);
  const [x3, y3] = polar(INNER_RADIUS, startAngle);
  return [
    `M ${x0} ${y0}`,
    `A ${OUTER_RADIUS} ${OUTER_RADIUS} 0 ${largeArc} 1 ${x1} ${y1}`,
    `L ${x2} ${y2}`,
    `A ${INNER_RADIUS} ${INNER_RADIUS} 0 ${largeArc} 0 ${x3} ${y3}`,
    'Z',
  ].join(' ');
}

function sliceTitle(slice) {
  const summary = `${slice.label} — ${fmtDur(slice.hours)} (${slice.pct.toFixed(0)}%)`;
  if (!slice.items?.length) return summary;
  return `${summary}: ${slice.items
    .map((item) => `${item.label} — ${fmtDur(item.hours)}`)
    .join(', ')}`;
}

export function DonutChart({ title, slices, total }) {
  const tooltipIdPrefix = useId();

  if (!slices.length) {
    return <div className="pie-empty">No hours in the selected period</div>;
  }

  let angle = -Math.PI / 2;
  const segments = slices.map((slice) => {
    const start = angle;
    angle += (slice.pct / 100) * Math.PI * 2;
    return { slice, start, end: angle };
  });

  return (
    <div className="pie-layout">
      <svg
        className="pie-svg"
        viewBox="0 0 200 200"
        role="img"
        aria-label={`${title}: ${slices.map(sliceTitle).join(', ')}`}
      >
        {slices.length === 1 ? (
          <circle
            cx={CENTER}
            cy={CENTER}
            r={(OUTER_RADIUS + INNER_RADIUS) / 2}
            fill="none"
            stroke={slices[0].color}
            strokeWidth={OUTER_RADIUS - INNER_RADIUS}
          >
            <title>{sliceTitle(slices[0])}</title>
          </circle>
        ) : (
          segments.map(({ slice, start, end }) => (
            <path
              key={slice.id}
              className="pie-slice"
              d={arcPath(start, end)}
              fill={slice.color}
            >
              <title>{sliceTitle(slice)}</title>
            </path>
          ))
        )}
        <text className="pie-center-value" x={CENTER} y={CENTER - 2}>
          {fmtDur(total)}
        </text>
        <text className="pie-center-sub" x={CENTER} y={CENTER + 16}>
          {(total / 8).toFixed(1)} MD
        </text>
      </svg>
      <div className="pie-legend">
        {slices.map((slice) => {
          const hasDetails = Boolean(slice.items?.length);
          const tooltipId = `${tooltipIdPrefix}-${slice.id}`;
          return (
            <div
              key={slice.id}
              className={`pie-legend-row${hasDetails ? ' has-details' : ''}`}
              data-tooltip={hasDetails ? undefined : slice.label}
              tabIndex={hasDetails ? 0 : undefined}
              aria-describedby={hasDetails ? tooltipId : undefined}
            >
              <span
                className="pie-legend-chip"
                style={{ background: slice.color }}
              />
              <span className="truncate">{slice.label}</span>
              <strong>{fmtDur(slice.hours)}</strong>
              <span className="pie-legend-pct">{slice.pct.toFixed(0)}%</span>
              {hasDetails && (
                <div
                  id={tooltipId}
                  className="pie-other-tooltip"
                  role="tooltip"
                >
                  <strong>Included in {slice.label}</strong>
                  <div className="pie-other-list">
                    {slice.items.map((item) => (
                      <div key={item.id} className="pie-other-row">
                        <span>{item.label}</span>
                        <strong>{fmtDur(item.hours)}</strong>
                        <span>{item.pct.toFixed(0)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ChartCard({ title, options, mode, onModeChange, slices, total }) {
  const active = options.find(([key]) => key === mode) || options[0];
  return (
    <section className="card pie-card">
      <div className="pie-card-head">
        <h2>{title}</h2>
        <div className="segmented inline" aria-label={`${title} split`}>
          {options.map(([key, label]) => (
            <button
              key={key}
              className={mode === key ? 'active' : ''}
              onClick={() => onModeChange(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <DonutChart title={active[1]} slices={slices} total={total} />
    </section>
  );
}

// Two donut cards for the Reports overview: hours split by client/project and
// by team/person, driven by the already-filtered report payload.
export function ReportBreakdownCharts({ report }) {
  const [clientMode, setClientMode] = useSessionState(
    'reports.chart.clients',
    'clients',
    CLIENT_MODES,
  );
  const [teamMode, setTeamMode] = useSessionState(
    'reports.chart.teams',
    'teams',
    TEAM_MODES,
  );

  const clientSplit = useMemo(
    () =>
      buildSlices(
        report.clients,
        (row) => row.client.name,
        (row) => row.client.id,
      ),
    [report.clients],
  );
  const projectSplit = useMemo(
    () =>
      // Unpaid absence is not in the total, so it isn't a slice of it.
      buildSlices(
        report.projects.filter((row) => row.workType !== 'unpaid-absence'),
        (row) => `${row.project.code} ${row.project.name}`,
        (row) => row.project.id,
      ),
    [report.projects],
  );
  const teamSplit = useMemo(
    () =>
      buildSlices(
        buildTeamRows(report.people),
        (row) => row.teamName,
        (row) => row.teamId || 'none',
      ),
    [report.people],
  );
  const peopleSplit = useMemo(
    () =>
      buildSlices(
        report.people,
        (row) => row.user.name,
        (row) => row.user.id,
      ),
    [report.people],
  );

  const clientData = clientMode === 'clients' ? clientSplit : projectSplit;
  const teamData = teamMode === 'teams' ? teamSplit : peopleSplit;

  return (
    <div className="report-charts-grid">
      <ChartCard
        title="Clients & projects"
        options={[
          ['clients', 'By client'],
          ['projects', 'By project'],
        ]}
        mode={clientMode}
        onModeChange={setClientMode}
        slices={clientData.slices}
        total={clientData.total}
      />
      <ChartCard
        title="Teams & people"
        options={[
          ['teams', 'By team'],
          ['people', 'By person'],
        ]}
        mode={teamMode}
        onModeChange={setTeamMode}
        slices={teamData.slices}
        total={teamData.total}
      />
    </div>
  );
}
